'use client';

import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import type { QueryMeta } from '@unifyapps/network/utils/fetch';
import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';
import type {
  AppRouterInstance,
  PrefetchOptions,
} from 'next/dist/shared/lib/app-router-context.shared-runtime';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- needed here
import { useRouter as useNextRouter, useParams } from 'next/navigation';
import { useTransitionRouter as useNextViewTransitionsRouter } from 'next-view-transitions';
import { useMemo } from 'react';
import { useClientContext } from '../context/client/ClientContext';
import { type ExtendedNavigateOptions, processUrl } from './utils/processUrl';
import { useIsViewTransitionsEnabled } from './ViewTransitions';

export const DEFAULT_FALLBACK_ROUTE = '/';
export const UAPrefetchKind = PrefetchKind;

export interface ExtendPrefetchOptions extends PrefetchOptions {
  pId?: number;
  source?: string;
  sourcePayload?: Record<string, unknown>;
  persistSourceInfo?: boolean;
  useNativeRouter?: boolean;
}

type NavigateWithProjectOptions = (url: string, options?: ExtendedNavigateOptions) => void;
type PrefetchWithProjectOptions = (url: string, options?: ExtendPrefetchOptions) => void;

export type ProcessURLMiddleware = (
  route: string,
  options?: ExtendedNavigateOptions,
  context?: { projectSlug?: string; queryMeta?: QueryMeta },
) => string;

const useRouter: () => Omit<AppRouterInstance, 'push' | 'replace' | 'back' | 'prefetch'> & {
  push: NavigateWithProjectOptions;
  replace: NavigateWithProjectOptions;
  back: (fallbackUrl?: string) => void;
  prefetch: PrefetchWithProjectOptions;
} = () => {
  const isViewTransitionsEnabled = useIsViewTransitionsEnabled();
  const useRouter = isViewTransitionsEnabled ? useNextViewTransitionsRouter : useNextRouter;
  const router = useRouter();

  const nextRouter = useNextRouter();

  const { projectSlug } = useParams<{ projectSlug?: string }>();
  const queryMeta = useGetQueryMeta();
  const clientContextValue = useClientContext();
  const client = clientContextValue?.client;

  return useMemo(() => {
    return {
      ...router,
      //we'll only do router.back if we have 2 or more pages in window.history, otherwise we'll do a router.replace with a fallbackUrl or DEFAULT_FALLBACK_ROUTE
      back: (fallbackUrl?: string) => {
        return window.history.length > 1
          ? router.back()
          : router.replace(fallbackUrl ?? DEFAULT_FALLBACK_ROUTE);
      },
      push: (url, options) => {
        const newUrl = processUrl(url, options, {
          projectSlug,
          client,
          queryMeta,
        });
        return router.push(newUrl, options);
      },
      replace: (url, options) => {
        const newUrl = processUrl(url, options, {
          projectSlug,
          client,
          queryMeta,
        });
        // we always want to use the next router that is without view  transitions in case of replaces because these are just url updates without needing to show a view transition
        return nextRouter.replace(newUrl, options);
      },
      prefetch: (url, options) => {
        const newUrl = processUrl(url, options, {
          projectSlug,
          client,
          queryMeta,
        });
        return router.prefetch(newUrl, options);
      },
    };
  }, [client, projectSlug, router, queryMeta, nextRouter]);
};

export default useRouter;
